import { Inject, Injectable } from '@angular/core';

import { ErrorInformation } from '@shared/models/error/error-information';
import { err, ok } from '@common/result/Result';

import { AsyncResult } from '@common/result/asyncResult';
import { EgiAppEntity } from '@/app/platform/platform-data/models/egi-app.entity';
import { EgiAppEntityStore } from '@/app/platform/platform-data/store/egi-app.entity.store';

import {
  EGI_APP_SYNCHRONIZER,
  EgiAppsSynchronizer,
  FaultyClickTroughConfiguration
} from '@/app/platform/platform-data/synchronizers/types';
import {
  EGI_APP_SERVICE,
  EgiAppService,
  NAVIGATION_ITEM_SERVICE,
  NavigationItemService
} from '@/app/platform/platform-data/services/types';
import { EGI_APP_QUERIES, EgiAppQueries } from '@/app/platform/platform-data/queries/types';
import { EgiAppActions } from '@/app/platform/platform-data/actions/types';
import { ErrorReason } from '@shared/models/error/error-reason';

@Injectable({ providedIn: 'root' })
export class EgiAppStoreActions implements EgiAppActions {
  constructor(
    @Inject(EGI_APP_QUERIES)
    private readonly query: EgiAppQueries,
    @Inject(EGI_APP_SERVICE)
    private readonly egiAppService: EgiAppService,
    private readonly egiAppStore: EgiAppEntityStore,
    @Inject(NAVIGATION_ITEM_SERVICE)
    private readonly navigationItemService: NavigationItemService,
    @Inject(EGI_APP_SYNCHRONIZER)
    private readonly egiAppsSynchronizer: EgiAppsSynchronizer
  ) {}

  async create(app: EgiAppEntity): AsyncResult<EgiAppEntity> {
    const result = await this.egiAppService.create(app);
    if (result.isValid) {
      await this.egiAppsSynchronizer.synchronizeApps();

      const egiApp = result.value as EgiAppEntity;
      const afterSyncResult = await this.egiAppService.getById(egiApp.id);
      afterSyncResult.ifSuccess(a => {
        this.egiAppStore.add(a);
      });

      return afterSyncResult;
    }

    return result;
  }

  async delete(app: EgiAppEntity): AsyncResult<EgiAppEntity> {
    const result = await this.egiAppService.delete(app);
    result.ifSuccess(egiApp => this.egiAppStore.remove(egiApp.id));
    return result;
  }

  async update(app: EgiAppEntity): AsyncResult<EgiAppEntity> {
    const result = await this.egiAppService.update(app);
    result.ifSuccess(egiApp => {
      egiApp.rootNavigationItem.sortDescendants();
      this.egiAppStore.update(egiApp.id, egiApp);
    });
    return result;
  }

  async reOrder(app: EgiAppEntity): AsyncResult<EgiAppEntity[]> {
    const result = await this.navigationItemService.reOrder(app.rootNavigationItem);
    if (!result.isValid) {
      return err();
    }

    return await this.reloadAll();
  }

  async reloadAll(): AsyncResult<EgiAppEntity[]> {
    const result = await this.egiAppService.getAll();

    result.ifSuccess(egiApps => {
      egiApps.forEach(a => a.rootNavigationItem?.sortDescendants());

      this.egiAppStore.set(egiApps);
    });

    return result;
  }

  async loadOne(id: string): AsyncResult<EgiAppEntity> {
    // TODO: implement
    return err([new ErrorInformation('Unimplemented!', ErrorReason.Unimplemented)]);
  }

  async reloadOne(id: string): AsyncResult<EgiAppEntity> {
    const result = await this.egiAppService.getById(id);

    result.ifSuccess(egiApp => {
      egiApp.rootNavigationItem?.sortDescendants();
      this.egiAppStore.update(egiApp.id, egiApp);
    });

    return result;
  }

  async loadAll(): AsyncResult<EgiAppEntity[]> {
    if (this.query.getHasCache()) {
      const existing = this.query.getAll();
      return ok(existing);
    }

    return this.reloadAll();
  }

  async synchronizeAll(): AsyncResult<
    EgiAppEntity[],
    ErrorInformation | ErrorInformation<FaultyClickTroughConfiguration[]>
  > {
    const result = await this.egiAppsSynchronizer.synchronizeApps();
    result.ifSuccess(apps => this.egiAppStore.set(apps));
    return result;
  }

  reset(): void {
    this.egiAppStore.reset();
  }
}
